import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { adminApi } from '../../lib/api';
import CompactCount from './CompactCount';
import { Wrench, Activity, FileText, Lightbulb, ChevronRight } from 'lucide-react';

// Order + labels for the 4 item_visits sections (backend VALID_TRENDING_SECTIONS).
const SECTIONS = [
  { key: 'tool', label: 'Tools', icon: Wrench },
  { key: 'simulation', label: 'Simulations', icon: Activity },
  { key: 'article', label: 'Articles', icon: FileText },
  { key: 'how_it_works', label: 'How It Works', icon: Lightbulb },
];

// item_id is the visited page's path (see PageVisitTracker) -- show its last segment
// as a readable label, e.g. /tools/cable-size-calculator -> "cable size calculator".
const pathLabel = (path) => {
  const last = path.split('/').filter(Boolean).pop() || path;
  return decodeURIComponent(last).replace(/[-_]+/g, ' ');
};

const VisitStatsCards = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminApi.getVisitStats()
      .then(setStats)
      .catch(() => setError(true));
  }, []);

  const bySection = Object.fromEntries((stats?.sections || []).map((s) => [s.section, s]));

  return (
    <section className="mb-10" data-testid="visit-stats">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <h2 className="font-chivo text-lg font-bold uppercase tracking-tight">Content Visits</h2>
        {stats && (
          <span className="text-sm text-muted-foreground">
            All time: <CompactCount value={stats.total} className="font-mono font-semibold text-foreground" />
          </span>
        )}
      </div>
      {error ? (
        <p className="text-sm text-destructive">Could not load visit stats.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const s = bySection[key];
            return (
              <Card key={key} className="border-2 h-full" data-testid={`visit-card-${key}`}>
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2">
                    <Icon className="h-4 w-4 text-autoxium-blue" />
                    <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
                  </div>
                  {s ? (
                    <CompactCount
                      value={s.total}
                      className="font-chivo text-3xl font-black"
                      data-testid={`visit-total-${key}`}
                    />
                  ) : (
                    <Skeleton className="h-9 w-20" />
                  )}
                  {s && s.top.length > 0 && (
                    <ol className="mt-4 space-y-1.5 text-sm">
                      {s.top.map((t, i) => (
                        <li key={t.item_id} className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">{i + 1}.</span>
                          <Link
                            to={t.item_id}
                            className="truncate capitalize hover:text-autoxium-blue flex-1 min-w-0"
                            title={t.item_id}
                          >
                            {pathLabel(t.item_id)}
                          </Link>
                          <CompactCount value={t.count} className="text-xs font-mono text-muted-foreground shrink-0" />
                        </li>
                      ))}
                    </ol>
                  )}
                  {s && s.top.length === 0 && (
                    <p className="mt-4 text-xs text-muted-foreground">No visits recorded yet.</p>
                  )}
                  {s && (
                    <Link
                      to={`/admin/visits?section=${key}`}
                      className="mt-auto pt-4 self-start inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-autoxium-blue hover:underline"
                      data-testid={`visit-view-all-${key}`}
                    >
                      View all <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default VisitStatsCards;
